<template>
  <v-tooltip :color="$colors.white" content-class="tooltip-container" location="top">
    <template #activator="{ props: activatorProps }">
      <div v-bind="activatorProps" class="d-flex align-center tooltip-content">
        <ui-icon size="13" :color="$colors.green" class="mr-2" icon="fas fa-info-circle" />
        <div class="label">{{ t('updated') }}</div>
      </div>
    </template>
    <div class="info-text" v-html="updateInfo"></div>
  </v-tooltip>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useColors, useI18n } from '@/composables';

type DateInfo = {
  updated?: string;
  created?: string;
  updatedBy?: string;
};

const props = withDefaults(
  defineProps<{
    data?: DateInfo;
    infoFormat?: string;
  }>(),
  {
    data: () => ({}),
    infoFormat: '',
  },
);

const { t } = useI18n();
const $colors = useColors();

const updateInfo = computed(() => {
  const { updated, created, updatedBy } = props.data ?? {};
  const timestamp = `${updated || created} (UTC)`;

  if (props.infoFormat === 'short') {
    return t('<b>Last Updated</b>{0}', [`&nbsp;<span>${timestamp}</span>`]);
  }

  if (updatedBy === 'Team Member') {
    return t('Updated by Team Member, {0}', [timestamp]);
  }

  const name = updatedBy || t('No Name');

  return t('<b>Last Updated:</b> {0} <span>{1}</span>', [
    `&nbsp;<span class="name">${name}</span>`,
    timestamp,
  ]);
});
</script>

<style lang="scss">
.v-overlay__content.tooltip-container {
  background: var(--color-white) !important;
  color: var(--color-graphite) !important;
  opacity: 1 !important;
  padding: 7px;
  border-radius: 5px;
  box-shadow: 0 4px 16px 0 var(--color-black20) !important;

  .info-text {
    color: var(--color-graphite);
    overflow: hidden;
    text-overflow: ellipsis;
  }
}
</style>

<style lang="scss" scoped>
.tooltip-content {
  height: 100%;
  cursor: default;
  margin-right: 8px;
  align-self: center;

  .label {
    text-transform: capitalize;
    color: var(--color-green);
    border-bottom: 1px dashed;

    @media all and (max-width: 400px) {
      display: none;
    }
  }
}
</style>
